import * as signalR from "@microsoft/signalr";
import CryptoJS from "crypto-js";
import config from "../../config";
import { getAuthHeaders } from "../api/common";

// Backoff delays (ms) used when the SignalR automatic-reconnect schedule is
// exhausted and we need to schedule a full re-initialization from scratch.
const MANUAL_RECONNECT_DELAYS = [5000, 10000, 20000, 30000, 60000];

class MessageService {
  constructor() {
    this.connection = null;
    this.encryptionKey =
      process.env.REACT_APP_MESSAGE_ENCRYPTION_KEY || "your-fallback-key";
    this.callbacks = new Map();
    this.connectionState = "disconnected";
    this.connectionPromise = null;
    this.currentUserId = null;
    this.currentAuthToken = null;
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
  }

  // Schedule a full re-initialization after the SignalR automatic-reconnect
  // schedule is exhausted (onclose fires). Uses exponential backoff so we
  // don't hammer the server after a prolonged outage.
  _scheduleManualReconnect() {
    if (this.reconnectTimer) return;
    const delay =
      MANUAL_RECONNECT_DELAYS[
        Math.min(this.reconnectAttempts, MANUAL_RECONNECT_DELAYS.length - 1)
      ];
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      if (!this.currentUserId) return;
      const token = localStorage.getItem("velaToken");
      if (!token) return;
      this.connection = null; // force fresh connection in initialize()
      await this.initialize(this.currentUserId, token);
    }, delay);
  }

  // Fetch message history
  async fetchMessageHistory() {
    try {
      const historyUrl = `${config.api.url}/message/history`;

      const res = await fetch(historyUrl, {
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        if (res.status === 404) {
          const altUrl = `${config.api.url}/messages`;
          const altRes = await fetch(altUrl, { headers: getAuthHeaders() });
          if (!altRes.ok) {
            this.notifyListeners("messagesLoaded", []);
            return [];
          }
          const altMessages = await altRes.json();
          this.notifyListeners("messagesLoaded", altMessages);
          return altMessages;
        }
        throw new Error(
          `Failed to fetch message history: ${res.status} ${res.statusText}`
        );
      }

      const response = await res.json();
      const messages = response.messages || [];
      const userCache = new Map();

      const fetchUserData = async (userId) => {
        if (userCache.has(userId)) return userCache.get(userId);
        try {
          const r = await fetch(`${config.api.url}/user/${userId}`, {
            headers: getAuthHeaders(),
          });
          if (r.ok) {
            const userData = await r.json();
            const name =
              userData.firstName && userData.lastName
                ? `${userData.firstName} ${userData.lastName}`
                : userData.firstName || userData.lastName || "Unknown";
            userCache.set(userId, name);
            return name;
          } else if (r.status === 403) {
            const fallback = `User ${userId.slice(0, 8)}...`;
            userCache.set(userId, fallback);
            return fallback;
          }
        } catch (error) {
          console.error("Error fetching user data for:", userId, error);
        }
        userCache.set(userId, "Unknown");
        return "Unknown";
      };

      const userIdsToFetch = new Set();
      messages.forEach((msg) => {
        if (msg.senderId && !msg.sender) userIdsToFetch.add(msg.senderId);
        if (msg.recipientId && !msg.recipient) userIdsToFetch.add(msg.recipientId);
      });

      const userIds = Array.from(userIdsToFetch);
      const batchSize = 3;
      const delay = 150;
      for (let i = 0; i < userIds.length; i += batchSize) {
        const batch = userIds.slice(i, i + batchSize);
        await Promise.all(batch.map((uid) => fetchUserData(uid)));
        if (i + batchSize < userIds.length) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }

      const formattedMessages = messages.map((msg) => {
        let timestamp;
        try {
          timestamp = msg.timestamp.endsWith("Z")
            ? new Date(msg.timestamp).toISOString()
            : new Date(msg.timestamp + "Z").toISOString();
        } catch {
          timestamp = new Date().toISOString();
        }

        const senderName = msg.sender
          ? `${msg.sender.firstName || ""} ${msg.sender.lastName || ""}`.trim() || "Unknown"
          : userCache.get(msg.senderId) || "Unknown";

        const recipientName = msg.recipient
          ? `${msg.recipient.firstName || ""} ${msg.recipient.lastName || ""}`.trim() || "Unknown"
          : userCache.get(msg.recipientId) || "Unknown";

        const rawAttachments = msg.attachments || msg.Attachments;
        const attachments = Array.isArray(rawAttachments)
          ? rawAttachments.map((att) => ({
              id: att.id || att.Id,
              fileName: att.fileName || att.FileName,
              contentType: att.contentType || att.ContentType,
              fileSize: att.fileSize || att.FileSize,
              url: att.url || att.Url,
            }))
          : [];

        return {
          id: msg.id,
          senderId: msg.senderId,
          senderName,
          recipientId: msg.recipientId,
          recipientName,
          content: msg.encryptedContent,
          timestamp,
          read: msg.isRead,
          isOutgoing: msg.senderId === this.currentUserId,
          otherPartyId:
            msg.senderId === this.currentUserId ? msg.recipientId : msg.senderId,
          otherPartyEmail:
            msg.senderId === this.currentUserId
              ? msg.recipient?.email || ""
              : msg.sender?.email || "",
          attachments,
        };
      });

      this.notifyListeners("messagesLoaded", formattedMessages);
      return formattedMessages;
    } catch (error) {
      console.error("Error fetching message history:", error);
      this.notifyListeners("messagesLoaded", []);
      return [];
    }
  }

  // Initialize (or re-initialize) the SignalR connection.
  async initialize(userId, authToken) {
    this.currentUserId = userId;
    const tokenChanged = this.currentAuthToken !== null && this.currentAuthToken !== authToken;
    this.currentAuthToken = authToken;

    // Reset manual-reconnect backoff on an explicit initialize() call.
    this.reconnectAttempts = 0;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // A practice switch keeps the same global user ID but replaces the contextual
    // token. Never reuse the socket authenticated for the previous practice.
    if (tokenChanged && this.connection) {
      const previousConnection = this.connection;
      this.connection = null;
      try {
        await previousConnection.stop();
      } catch {
        // ignore stale connection shutdown failures
      }
    }

    // Deduplicate concurrent initialize() calls.
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Already connected — nothing to do.
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return true;
    }

    this.connectionPromise = (async () => {
      try {
        // Tear down any stale connection first.
        if (this.connection) {
          try {
            await this.connection.stop();
          } catch {
            // ignore
          }
        }

        this.connection = new signalR.HubConnectionBuilder()
          .withUrl(config.api.messageHubUrl, {
            // Always read the current token from storage so reconnects after
            // a token refresh use a valid credential.
            accessTokenFactory: () =>
              localStorage.getItem("velaToken") || authToken,
            withCredentials: false,
          })
          .withHubProtocol(new signalR.JsonHubProtocol())
          // Extended retry schedule; covers transient blips up to ~2 minutes
          // before onclose fires and the manual backoff kicks in.
          .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000, 60000])
          .configureLogging(signalR.LogLevel.Error)
          .build();

        this.connection.onclose(() => {
          this.connectionState = "disconnected";
          this.notifyListeners("connectionStateChanged", this.connectionState);
          // Automatic reconnect gave up — schedule a full re-init with backoff.
          if (localStorage.getItem("velaToken") && this.currentUserId) {
            this._scheduleManualReconnect();
          }
        });

        this.connection.onreconnecting(() => {
          this.connectionState = "reconnecting";
          this.notifyListeners("connectionStateChanged", this.connectionState);
        });

        this.connection.onreconnected(async () => {
          this.connectionState = "connected";
          this.reconnectAttempts = 0;
          this.notifyListeners("connectionStateChanged", this.connectionState);
          // Re-register with the hub so the server can route messages to this
          // new connection ID.
          try {
            await this.connection.invoke(
              "RegisterUser",
              this.currentUserId.toString()
            );
          } catch (err) {
            console.warn("RegisterUser on reconnect failed:", err);
          }
          await this.fetchMessageHistory();
        });

        this.connection.on("ReceiveMessage", this.handleIncomingMessage);
        this.connection.on("MessageSent", this.handleIncomingMessage);
        this.connection.on(
          "UserTyping",
          this.notifyListeners.bind(this, "userTyping")
        );
        this.connection.on("UserStatusChanged", (uid, isOnline) => {
          this.notifyListeners("userStatusChanged", { userId: uid, isOnline });
        });
        this.connection.on("RegistrationComplete", () => {
          this.connectionState = "connected";
          this.notifyListeners("connectionStateChanged", this.connectionState);
        });
        // Fired by the hub when the recipient marks our messages as read.
        // Lets the sender's UI reflect read status in real time.
        this.connection.on("MessagesRead", (readIds) => {
          this.notifyListeners("messagesRead", readIds);
        });
        this.connection.on("PracticeInboxChanged", (change) => {
          this.notifyListeners("practiceInboxChanged", change);
        });
        this.connection.on("CareTeamConversationChanged", (change) => {
          this.notifyListeners("careTeamConversationChanged", change);
        });

        await this.connection.start();
        await this.connection.invoke("RegisterUser", userId.toString());
        await this.fetchMessageHistory();

        this.connectionState = "connected";
        this.notifyListeners("connectionStateChanged", this.connectionState);
        return true;
      } catch (err) {
        this.connectionState = "error";
        this.notifyListeners("connectionStateChanged", this.connectionState);
        if (err.message?.includes("RegisterUser")) {
          console.warn("RegisterUser failed, keeping connection for retry:", err);
        } else {
          this.connection = null;
        }
        return false;
      } finally {
        this.connectionPromise = null;
      }
    })();

    return this.connectionPromise;
  }

  getConnectionState() {
    return this.connectionState;
  }

  encryptMessage(message) {
    return CryptoJS.AES.encrypt(
      JSON.stringify(message),
      this.encryptionKey
    ).toString();
  }

  decryptMessage(encryptedMessage) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedMessage, this.encryptionKey);
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (err) {
      console.error("Decryption Error:", err);
      return null;
    }
  }

  async sendMessage(recipientId, content, attachmentIds = []) {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      console.error("Cannot send message: No active SignalR connection");
      return false;
    }
    try {
      if (attachmentIds.length > 0) {
        await this.connection.invoke(
          "SendMessageWithAttachments",
          recipientId.toString(),
          content,
          attachmentIds
        );
      } else {
        await this.connection.invoke(
          "SendMessage",
          recipientId.toString(),
          content
        );
      }
      return true;
    } catch (err) {
      console.error("Send Message Error:", err);
      return false;
    }
  }

  handleIncomingMessage = (message) => {
    if (!message) {
      console.error("Received null message");
      return;
    }

    let timestamp;
    try {
      const raw = message.timestamp || message.Timestamp;
      if (raw) {
        timestamp = raw.endsWith("Z")
          ? new Date(raw).toISOString()
          : new Date(raw + "Z").toISOString();
      } else {
        timestamp = new Date().toISOString();
      }
    } catch {
      timestamp = new Date().toISOString();
    }

    const rawAttachments = message.attachments || message.Attachments;
    const attachments = Array.isArray(rawAttachments)
      ? rawAttachments.map((att) => ({
          id: att.id || att.Id,
          fileName: att.fileName || att.FileName,
          contentType: att.contentType || att.ContentType,
          fileSize: att.fileSize || att.FileSize,
          url: att.url || att.Url,
        }))
      : [];

    this.notifyListeners("messageReceived", {
      id: message.id || message.Id,
      senderId: message.senderId || message.SenderId,
      recipientId: message.recipientId || message.RecipientId,
      organizationId: message.organizationId || message.OrganizationId,
      conversationId: message.conversationId || message.ConversationId,
      content: message.content || message.Content || message.EncryptedContent,
      timestamp,
      read: message.read || message.isRead || message.IsRead || false,
      attachments,
    });
  };

  async sendTypingIndicator(recipientId) {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      return;
    }
    try {
      await this.connection.invoke("UserTyping", recipientId.toString());
    } catch (err) {
      console.error("Typing Indicator Error:", err);
    }
  }

  async markMessagesAsRead(messageIds) {
    try {
      const response = await fetch(`${config.api.url}/message/mark-read`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ messageIds }),
      });
      if (!response.ok) {
        console.error(
          "Failed to mark messages as read:",
          response.status,
          response.statusText
        );
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error marking messages as read:", error);
      return false;
    }
  }

  on(event, callback) {
    if (!this.callbacks.has(event)) {
      this.callbacks.set(event, new Set());
    }
    this.callbacks.get(event).add(callback);
  }

  off(event, callback) {
    if (this.callbacks.has(event)) {
      if (callback) {
        this.callbacks.get(event).delete(callback);
      } else {
        this.callbacks.delete(event);
      }
    }
  }

  notifyListeners(event, data) {
    if (this.callbacks.has(event)) {
      this.callbacks.get(event).forEach((cb) => {
        try {
          cb(data);
        } catch (err) {
          console.error("Error in listener callback:", err);
        }
      });
    }
  }

  async disconnect() {
    // Cancel any pending manual reconnect so we don't re-connect after
    // an intentional disconnect (e.g. logout).
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    if (this.connection) {
      // Detach synchronously so a context-switch initialization can safely create
      // the replacement while this old socket finishes shutting down.
      const connectionToStop = this.connection;
      this.connection = null;
      await connectionToStop.stop();
      this.connectionState = "disconnected";
      this.notifyListeners("connectionStateChanged", this.connectionState);
    }
  }
}

export const messageService = new MessageService();
